import log from '$$log';
import ganttFilterHelper from '$$helpers/gantt_filter';
import routerHelper from '$$routerHelper';

class Filter {
  #instance = null;

  foundTasksMap = {};

  indicesMap = {};

  filterApplied = false;

  init(instance) {
    this.#instance = instance;
    this.#resetData();
  }

  run() {
    const filterData = this.#instance.ganttDataApi.getCurrentFilter()?.properties;
    const hasFilterValues = filterData && Object.values(filterData).some(value => value || ((typeof value === 'string' || Array.isArray(value)) && value.length > 0));

    const ganttData =  this.#instance.serialize();

    if (filterData && hasFilterValues) {
      if (!routerHelper.isWorkloadViewRoute() && ganttData.data.length) {
        this.#applyFilter(filterData, ganttData);
      }
    } else {
      this.#resetData();
    }

    this.#instance.callEvent('workloadClearAndParse');

    const t = log.time('ganttRender.instance');
    this.#instance.render(); // Showing/hidding task happens in onBeforeTaskDisplay event
    t.end();
  }

  #resetData() {
    this.filterApplied = false;
    this.foundTasksMap = {};
    this.indicesMap = {};
  }

  #addFoundTask(task, tasks) {
    this.foundTasksMap[task.id] = task;

    if (task.type !== this.#instance.config.types.button) {
      tasks.forEach(taskEntry => {
        const foundTaskIndices = this.indicesMap[task.id];
        const taskEntryIndices = this.indicesMap[taskEntry.id];

        if (taskEntryIndices?.left < foundTaskIndices?.left && taskEntryIndices?.right > foundTaskIndices?.right) {
          this.foundTasksMap[taskEntry.id] = taskEntry;
        }
      });
    }
  }

  #removeExtraButtons() {
    Object.values(this.foundTasksMap).forEach(task => {
      if (task.type === this.#instance.config.types.button) {
        const hasParent = !!this.foundTasksMap[task.parent];

        if (!hasParent) {
          delete this.foundTasksMap[task.id];
        }
      }
    });

    if (Object.keys(this.foundTasksMap).length <= 2) {
      Object.values(this.foundTasksMap).forEach(task => {
        if (task.type === this.#instance.config.types.button) {
          delete this.foundTasksMap[task.id];
        }
      });
    }
  }

  #applyFilter(filterData, ganttData) {
    this.#resetData();
    this.#buildIndicesMap(ganttData.data);

    const filterFunction = ganttFilterHelper.getFilter(
      { options: filterData },
      ganttData,
      task => {
        this.#addFoundTask(task, ganttData.data);
      },
    );

    ganttData.data.forEach(task => filterFunction(task, this.indicesMap));

    this.#removeExtraButtons();
    this.filterApplied = true;

    if (Object.keys(this.foundTasksMap).length > 2) {
      this.#instance.ganttDataApi.setFilterIsNotFound(false);
    } else {
      const isNotFound = !Object.keys(_.omitBy(this.foundTasksMap, task => !task.parent || task.type === this.#instance.config.types.button)).length;

      this.#instance.ganttDataApi.setFilterIsNotFound(isNotFound);
    }
  }

  #buildIndicesMap(tasks) {
    let counterIndex = 0;
    const reserve = taskId => {
      const task = this.#instance.getTask(taskId);

      counterIndex += 1;
      if (!this.indicesMap[taskId]) {
        this.indicesMap[taskId] = { left: null, right: null };
      }
      this.indicesMap[taskId].left = counterIndex;

      if (task.type !== this.#instance.config.types.project) {
        counterIndex += 1;
        this.indicesMap[taskId].right = counterIndex;

        return;
      }

      const childrenIds = this.#instance.getChildren(taskId);

      if (_.isEmpty(childrenIds)) {
        counterIndex += 1;
        this.indicesMap[taskId].right = counterIndex;

        return;
      }

      childrenIds.forEach(reserve);

      counterIndex += 1;
      this.indicesMap[taskId].right = counterIndex;
    };

    const rootTask = tasks.find(task => !task.parent);

    if (!rootTask) {
      throw new Error('Cannot find root task');
    }

    reserve(rootTask.id);
  }

  onParse() {
    if (this.#instance.ganttDataApi.isFilterActive() && !this.#instance.ganttDataApi.isHistory()) {
      this.run();
    }
  }

  onAfterTaskAdd(id, task) {
    if (this.filterApplied) {
      this.foundTasksMap[id] = task;
    }
  }

  onTaskIdChange(id, newId) {
    if (this.foundTasksMap[id]) {
      this.foundTasksMap[newId] = this.foundTasksMap[id];
      delete this.foundTasksMap[id];
    }

    if (this.indicesMap[id]) {
      this.indicesMap[newId] = this.indicesMap[id];
      delete this.indicesMap[id];
    }
  }
}

export default new Filter();
